import React, { useState, useEffect, useMemo } from 'react';
import { parseLocalDate } from '@/utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Calendar, Clock, AlertCircle, CheckCircle2, Loader2, Trophy, Swords, Coffee } from 'lucide-react';

import LeagueMatchCarousel from './LeagueMatchCarousel';
import EliminationMatchCard from './EliminationMatchCard';
import TennisChallengeCard from './TennisChallengeCard';
import { LeagueScheduledMatch } from '@/hooks/useLeagueMatches';
import { EliminationMatch } from '@/hooks/useLeagueEliminationMatches';
import type { TennisChallengeEncounter } from '@/hooks/useTennisChallengeEncounters';
import { useLanguage } from '@/contexts/LanguageContext';

type EliminationPhase = 'octavos' | 'cuartos' | 'semifinal' | 'final';

type SelectionType = 
  | { type: 'encounter'; value: number }
  | { type: 'elimination'; value: EliminationPhase }
  | { type: 'rest'; value: number };

// Mapeo de fases a encounter_number en league_scheduled_matches
const phaseToEncounterNumber: Record<EliminationPhase, number> = {
  'octavos': 1000,
  'cuartos': 1001,
  'semifinal': 1002,
  'final': 1003
};

interface EncounterResultsSectionProps {
  selectedEncounter: number;
  onEncounterChange: (encounter: number) => void;
  availableEncounters: number[];
  encounterStatus: 'waiting_previous' | 'not_scheduled' | 'completed' | 'in_progress' | 'scheduled';
  userMatchForEncounter: any;
  userMatchesForEncounter: any[] | null;
  userTeam: { player1: string; player2?: string } | null;
  opponentTeam: { player1: string; player2?: string } | null;
  weeks: any[];
  league: any;
  userAttendanceStatus: 'confirmed' | 'not_attending' | 'pending';
  opponentAttendanceStatus?: 'confirmed' | 'not_attending' | 'pending';
  isLoadingMatches: boolean;
  onWeekChange: (weekNum: number) => void;
  eliminationPhase?: EliminationPhase;
  hasEliminationPhase?: boolean;
  allScheduledMatches?: LeagueScheduledMatch[];
  isTennisSingles?: boolean;
  tennisChallengeEncounter?: TennisChallengeEncounter | null;
  challengeType?: string;
  challengePositionsAbove?: number | null;
  tennisSinglesEliminationMatches?: Record<string, EliminationMatch[]>;
  weekLabels?: string[];
  restWeeks?: number[];
  chronoWeekMapping?: Array<{ chronoWeek: number; type: 'encounter' | 'rest'; encounterNumber?: number }>;
  selectedChronoWeek?: number;
  hideStatusBadge?: boolean;
  padelStatusLabels?: boolean;
  padelSinglesLayout?: boolean;
  currentUserRegistrationId?: string | null;
  currentUserName?: string | null;
}

// Helper to get elimination phases based on starting phase
const getEliminationPhaseKeys = (startPhase: EliminationPhase): EliminationPhase[] => {
  const allPhases: EliminationPhase[] = ['octavos', 'cuartos', 'semifinal', 'final'];
  const startIndex = allPhases.indexOf(startPhase);
  return startIndex >= 0 ? allPhases.slice(startIndex) : [];
};

const EncounterResultsSection: React.FC<EncounterResultsSectionProps> = ({
  selectedEncounter,
  onEncounterChange,
  availableEncounters,
  encounterStatus,
  userMatchForEncounter,
  userMatchesForEncounter,
  userTeam,
  opponentTeam,
  weeks,
  league,
  userAttendanceStatus,
  opponentAttendanceStatus,
  isLoadingMatches,
  onWeekChange,
  eliminationPhase,
  hasEliminationPhase,
  allScheduledMatches,
  isTennisSingles,
  tennisChallengeEncounter,
  challengeType,
  challengePositionsAbove,
  tennisSinglesEliminationMatches,
  weekLabels,
  restWeeks,
  chronoWeekMapping,
  selectedChronoWeek,
  hideStatusBadge = false,
  padelStatusLabels = false,
  padelSinglesLayout = false,
  currentUserRegistrationId,
  currentUserName
}) => {
  const { t, language } = useLanguage();

  const phaseLabels: Record<EliminationPhase, string> = {
    'octavos': t('league.roundOf16'),
    'cuartos': t('league.quarterFinals'),
    'semifinal': t('league.semiFinals'),
    'final': t('league.final'),
  };

  const translateCourtName = (name: string): string => {
    if (language === 'en') return name.replace(/^Cancha\b/i, 'Court');
    return name;
  };

  const [selection, setSelection] = useState<SelectionType>({ type: 'encounter', value: selectedEncounter });

  useEffect(() => {
    if (selection.type === 'encounter' && selection.value !== selectedEncounter) {
      setSelection({ type: 'encounter', value: selectedEncounter });
    }
  }, [selectedEncounter]);

  useEffect(() => {
    if (!selectedChronoWeek || !chronoWeekMapping) return;
    const entry = chronoWeekMapping.find(m => m.chronoWeek === selectedChronoWeek);
    if (!entry) return;
    if (entry.type === 'rest') {
      setSelection({ type: 'rest', value: entry.chronoWeek });
    } else if (entry.type === 'encounter' && entry.encounterNumber) {
      setSelection({ type: 'encounter', value: entry.encounterNumber });
      onEncounterChange(entry.encounterNumber);
    }
  }, [selectedChronoWeek, chronoWeekMapping]);

  const eliminationMatches: EliminationMatch[] = useMemo(() => {
    if (selection.type !== 'elimination') return [];
    const phase = selection.value;

    if (isTennisSingles && tennisSinglesEliminationMatches && tennisSinglesEliminationMatches[phase]?.length > 0) {
      return tennisSinglesEliminationMatches[phase];
    }

    if (!allScheduledMatches) return [];

    const encounterNumber = phaseToEncounterNumber[phase];
    const phaseMatches = allScheduledMatches.filter(m => m.encounter_number === encounterNumber);
    if (phaseMatches.length === 0) return [];

    return phaseMatches.map((match) => {
      const team1Name = match.team1_player1_name 
        ? (match.team1_player2_name 
            ? `${match.team1_player1_name} / ${match.team1_player2_name}` 
            : match.team1_player1_name)
        : null;
      const team2Name = match.team2_player1_name 
        ? (match.team2_player2_name 
            ? `${match.team2_player1_name} / ${match.team2_player2_name}` 
            : match.team2_player1_name)
        : null;

      let formattedDate: string | null = null;
      if (match.scheduled_date) {
        const date = new Date(match.scheduled_date + 'T00:00:00');
        formattedDate = date.toLocaleDateString(language === 'en' ? 'en-US' : 'es-ES', { 
          day: 'numeric', month: 'short' 
        });
      }

      const formattedTime = match.scheduled_time ? match.scheduled_time.slice(0, 5) : null;

      return {
        id: match.id,
        phase: phase,
        matchNumber: match.match_number,
        team1: team1Name,
        team2: team2Name,
        score: null,
        winner: null,
        scheduledDate: formattedDate,
        scheduledTime: formattedTime,
        court: match.court_number ? translateCourtName(`Cancha ${match.court_number}`) : null,
        isUserMatch: false,
        isCompleted: match.status === 'completed'
      };
    }).sort((a, b) => a.matchNumber - b.matchNumber);
  }, [selection, allScheduledMatches, isTennisSingles, tennisSinglesEliminationMatches, language]);

  const isLoadingElimination = isLoadingMatches && selection.type === 'elimination';

  const availableEliminationPhases = hasEliminationPhase && eliminationPhase 
    ? getEliminationPhaseKeys(eliminationPhase).map(key => ({ key, label: phaseLabels[key] }))
    : [];

  const encounterDateLabel = useMemo(() => {
    if (!isTennisSingles || !weekLabels || weekLabels.length === 0 || selection.type !== 'encounter') return null;
    const rw = restWeeks || [];
    let encounterCount = 0;
    for (let i = 0; i < weekLabels.length; i++) {
      if (!rw.includes(i + 1)) {
        encounterCount++;
        if (encounterCount === selection.value) return weekLabels[i];
      }
    }
    return null;
  }, [isTennisSingles, weekLabels, restWeeks, selection]);

  const handleSelectionChange = (value: string) => {
    if (value.startsWith('rest_')) {
      const weekNum = parseInt(value.replace('rest_', ''), 10);
      setSelection({ type: 'rest', value: weekNum });
      return;
    }
    if (value.startsWith('elim_')) {
      const phase = value.replace('elim_', '') as EliminationPhase;
      setSelection({ type: 'elimination', value: phase });
    } else {
      const encounterNum = parseInt(value, 10);
      setSelection({ type: 'encounter', value: encounterNum });
      onEncounterChange(encounterNum);
    }
  };

  const getCurrentSelectionValue = (): string => {
    if (selection.type === 'rest') return `rest_${selection.value}`;
    if (selection.type === 'elimination') return `elim_${selection.value}`;
    return selection.value.toString();
  };

  const getCurrentSelectionLabel = (): string => {
    if (selection.type === 'rest') {
      return `☕ ${t('league.restWeekNum').replace('{n}', String(selection.value))}`;
    }
    if (selection.type === 'elimination') {
      const phase = availableEliminationPhases.find(p => p.key === selection.value);
      return `🏆 ${phase?.label || selection.value}`;
    }
    return `${t('league.encounterLabel')} ${selection.value}`;
  };

  const getStatusBadge = () => {
    if (hideStatusBadge) return null;
    if (selection.type === 'rest') {
      return (
        <Badge className="bg-amber-100 text-amber-700 border-amber-300">
          <Coffee className="h-3 w-3 mr-1" />
          {t('league.restWeekTitle')}
        </Badge>
      );
    }

    if (selection.type === 'elimination') {
      if (isLoadingElimination) {
        return (
          <Badge className="bg-gray-100 text-gray-700 border-gray-300">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            {t('league.loading')}
          </Badge>
        );
      }
      const allCompleted = eliminationMatches.length > 0 && eliminationMatches.every(m => m.isCompleted);
      const hasTeams = eliminationMatches.some(m => m.team1 || m.team2);
      
      if (allCompleted) {
        return (
          <Badge className="bg-green-100 text-green-700 border-green-300">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            {t('league.completedStatus')}
          </Badge>
        );
      }
      if (hasTeams) {
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-300">
            <Calendar className="h-3 w-3 mr-1" />
            {t('league.scheduledStatus')}
          </Badge>
        );
      }
      return (
        <Badge className="bg-gray-100 text-gray-700 border-gray-300">
          <AlertCircle className="h-3 w-3 mr-1" />
          {t('league.toSchedule')}
        </Badge>
      );
    }

    switch (encounterStatus) {
      case 'completed':
        return (
          <Badge className="bg-green-100 text-green-700 border-green-300">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            {t('league.completedStatus')}
          </Badge>
        );
      case 'in_progress':
        return (
          <Badge className="bg-primary/10 text-primary border-primary/30">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            {t('league.inProgress')}
          </Badge>
        );
      case 'scheduled':
        if (isTennisSingles && challengeType) {
          const challengeTypeLabels: Record<string, string> = {
            'odds_vs_evens': t('league.oddsVsEvens'),
            'evens_vs_odds': t('league.evensVsOdds'),
            'position_range': challengePositionsAbove 
              ? t('league.positionRange').replace('{n}', String(challengePositionsAbove))
              : t('league.positionRangeGeneric'),
          };
          const label = challengeTypeLabels[challengeType] || challengeType;
          return (
            <Badge className="bg-amber-100 text-amber-700 border-amber-300">
              <Swords className="h-3 w-3 mr-1" />
              {label}
            </Badge>
          );
        }
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-300">
            <Calendar className="h-3 w-3 mr-1" />
            {t('league.scheduledStatus')}
          </Badge>
        );
      case 'waiting_previous':
        return (
          <Badge className="bg-orange-100 text-orange-700 border-orange-300">
            <Clock className="h-3 w-3 mr-1" />
            {t('league.waitingStatus')}
          </Badge>
        );
      case 'not_scheduled':
        return (
          <Badge className="bg-gray-100 text-gray-700 border-gray-300">
            <AlertCircle className="h-3 w-3 mr-1" />
            {t('league.toSchedule')}
          </Badge>
        );
    }
  };

  const renderEliminationContent = () => {
    if (isLoadingElimination) {
      return (
        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin mb-2" />
          <p>{t('league.loadingElimination')}</p>
        </div>
      );
    }

    if (!eliminationMatches || eliminationMatches.length === 0) {
      const phaseLabel = availableEliminationPhases.find(p => p.key === selection.value)?.label || selection.value;
      const matchCount = selection.value === 'final' ? 1 : selection.value === 'semifinal' ? 2 : selection.value === 'cuartos' ? 4 : 8;
      
      return (
        <div className="space-y-4">
          {Array.from({ length: matchCount }, (_, i) => (
            <div key={i} className="bg-card rounded-lg border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-yellow-500" />
                  <span className="text-sm font-medium text-muted-foreground">
                    {phaseLabel} {matchCount > 1 ? i + 1 : ''}
                  </span>
                </div>
                <Badge className="bg-gray-100 text-gray-700 border-gray-300">
                  {t('league.toSchedule')}
                </Badge>
              </div>
              
              <div className="text-center space-y-2 py-2">
                <p className="font-semibold text-muted-foreground italic">
                  {t('league.notAssigned')}
                </p>
                <div className="flex items-center justify-center gap-2">
                  <div className="h-px w-8 bg-border"></div>
                  <span className="text-sm text-muted-foreground font-medium">vs</span>
                  <div className="h-px w-8 bg-border"></div>
                </div>
                <p className="font-semibold text-muted-foreground italic">
                  {t('league.notAssigned')}
                </p>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {eliminationMatches.map(match => (
          <EliminationMatchCard 
            key={match.id}
            match={match}
            sport={league.sport}
          />
        ))}
      </div>
    );
  };

  const renderEncounterContent = () => {
    if (isTennisSingles) {
      if (isLoadingMatches) {
        return (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin mb-2" />
            <p>{t('league.loadingChallenges')}</p>
          </div>
        );
      }

      if (tennisChallengeEncounter) {
        return <TennisChallengeCard encounter={tennisChallengeEncounter} weekNumber={selectedEncounter} dateLabel={encounterDateLabel || undefined} />;
      }

      return (
        <div className="bg-muted/50 border border-border rounded-lg p-6 text-center">
          <Swords className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <h4 className="font-semibold text-foreground mb-2">
            {t('league.noChallenge')}
          </h4>
          <p className="text-muted-foreground text-sm">
            {t('league.noChallengeDesc')}
          </p>
        </div>
      );
    }

    if (isLoadingMatches) {
      return (
        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin mb-2" />
          <p>{t('league.loadingMatches')}</p>
        </div>
      );
    }

    if (encounterStatus === 'waiting_previous') {
      return (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 text-center">
          <Clock className="h-10 w-10 text-orange-500 mx-auto mb-3" />
          <h4 className="font-semibold text-orange-800 mb-2">
            {t('league.waitingResults').replace('{n}', String(selectedEncounter - 1))}
          </h4>
          <p className="text-orange-600 text-sm">
            {t('league.waitingResultsDesc')}
          </p>
        </div>
      );
    }

    if (encounterStatus === 'not_scheduled') {
      return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <Calendar className="h-10 w-10 text-gray-400 mx-auto mb-3" />
          <h4 className="font-semibold text-gray-700 mb-2">
            {t('league.encounterToSchedule')}
          </h4>
          <p className="text-gray-500 text-sm">
            {t('league.encounterToScheduleDesc')}
          </p>
        </div>
      );
    }

    // Unify single and multiple matches into carousel
    const matchesForCarousel = userMatchesForEncounter && userMatchesForEncounter.length > 0
      ? userMatchesForEncounter
      : userMatchForEncounter ? [userMatchForEncounter] : null;

    if (matchesForCarousel && matchesForCarousel.length > 0) {
      return (
        <LeagueMatchCarousel
          matches={matchesForCarousel}
          weeks={weeks}
          selectedWeek={selectedEncounter}
          onWeekChange={onWeekChange}
          userAttendanceStatus={userAttendanceStatus}
          opponentAttendanceStatus={opponentAttendanceStatus}
          isLoading={isLoadingMatches}
          padelStatusLabels={padelStatusLabels}
          padelSinglesLayout={padelSinglesLayout}
          currentUserRegistrationId={currentUserRegistrationId}
          currentUserName={currentUserName}
          eventId={league?.id}
          encounterNumber={selectedEncounter}
        />
      );
    }

    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
        <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
        <h4 className="font-semibold text-amber-800 mb-2">
          {t('league.noMatchesAssigned')}
        </h4>
        <p className="text-amber-600 text-sm">
          {t('league.noMatchesAssignedDesc')}
        </p>
      </div>
    );
  };

  return (
    <Card className="bg-card">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <Select value={getCurrentSelectionValue()} onValueChange={handleSelectionChange}>
            <SelectTrigger className="w-auto min-w-[180px] bg-background">
              <SelectValue placeholder={t('league.selectEncounter')} />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              {chronoWeekMapping && chronoWeekMapping.length > 0 ? (
                <>
                  {chronoWeekMapping.map(entry => (
                    entry.type === 'rest' ? (
                      <SelectItem key={`rest_${entry.chronoWeek}`} value={`rest_${entry.chronoWeek}`}>
                        ☕ {t('league.restWeekNum').replace('{n}', String(entry.chronoWeek))}
                      </SelectItem>
                    ) : (
                      <SelectItem key={entry.encounterNumber} value={entry.encounterNumber!.toString()}>
                        {t('league.encounterLabel')} {entry.encounterNumber}
                      </SelectItem>
                    )
                  ))}
                  {availableEliminationPhases.map(phase => (
                    <SelectItem key={`elim_${phase.key}`} value={`elim_${phase.key}`}>
                      🏆 {phase.label}
                    </SelectItem>
                  ))}
                </>
              ) : (
                <>
                  {availableEncounters.map(enc => (
                    <SelectItem key={enc} value={enc.toString()}>
                      {t('league.encounterLabel')} {enc}
                    </SelectItem>
                  ))}
                  {availableEliminationPhases.map(phase => (
                    <SelectItem key={`elim_${phase.key}`} value={`elim_${phase.key}`}>
                      🏆 {phase.label}
                    </SelectItem>
                  ))}
                </>
              )}
            </SelectContent>
          </Select>
          {getStatusBadge()}
        </div>

        <Separator className="mb-4" />

        {selection.type === 'rest' ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center space-y-2">
            <Coffee className="h-10 w-10 text-amber-500 mx-auto mb-3" />
            <h4 className="font-semibold text-amber-800">
              {t('league.restWeekTitle')}
            </h4>
            <p className="text-amber-600 text-sm">
              {t('league.noMatchThisWeek')}
            </p>
          </div>
        ) : selection.type === 'elimination' 
          ? renderEliminationContent() 
          : renderEncounterContent()
        }
      </CardContent>
    </Card>
  );
};

export default EncounterResultsSection;
